import React, { useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import contactImg from '../assets/img/contact-img.svg';
import 'animate.css';
import TrackVisibility from 'react-on-screen';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export const Contact = () => {
  const formInitialDetails = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  };

  const [formDetails, setFormDetails] = useState(formInitialDetails);
  const [buttonText, setButtonText] = useState('Send');
  const [submitting, setSubmitting] = useState(false);

  const onFormUpdate = (category, value) => {
    setFormDetails((prev) => ({
      ...prev,
      [category]: value
    }));
  };

  const sanitizedPhone = formDetails.phone.replace(/\D/g, '').slice(0, 10);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic client-side guardrails (optional)
    if (!formDetails.firstName || !formDetails.email || !formDetails.message) {
      toast.error('Please fill in First Name, Email, and Message.', { position: 'top-center' });
      return;
    }

    setSubmitting(true);
    setButtonText('Sending...');

    const payload = {
      name: `${formDetails.firstName} ${formDetails.lastName}`.trim(),
      email: formDetails.email,
      message: formDetails.message,
      phone: sanitizedPhone
    };

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      // Try to parse JSON; if it fails, fall back to text
      let data;
      try {
        data = await res.json();
      } catch {
        const txt = await res.text();
        data = { error: txt || 'Unexpected response from server' };
      }

      if (res.ok && (data?.ok || !data?.error)) {
        toast.success('Message sent successfully!', {
          position: 'top-center',
          style: { maxWidth: '300px' }
        });

        // If you enabled Ethereal in your local Function, you might get a previewUrl
        if (data.previewUrl) {
          console.log('Ethereal preview URL:', data.previewUrl);
        }

        // Reset form
        setFormDetails(formInitialDetails);
      } else {
        const reason = res.status === 404
          ? 'Contact endpoint not found. Start both frontend and backend with npm start.'
          : (data?.error || 'Something went wrong, please try again later.');
        toast.error(reason, { position: 'top-center' });
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Network error. Start `npm start` and ensure backend is running on port 5050.', { position: 'top-center' });
    } finally {
      setSubmitting(false);
      setButtonText('Send');
    }
  };

  return (
    <section className='contact' id='connect'>
      <Container>
        <Row className='align-items-center'>
          <Col size={12} md={6}>
            <TrackVisibility>
              {({ isVisible }) => (
                <img
                  className={isVisible ? 'animate__animated animate__zoomIn' : ''}
                  src={contactImg}
                  alt='Contact Us'
                  loading='lazy'
                  decoding='async'
                  width='560'
                  height='420'
                />
              )}
            </TrackVisibility>
          </Col>

          <Col size={12} md={6}>
            <TrackVisibility>
              {({ isVisible }) => (
                <div className={isVisible ? 'animate__animated animate__fadeIn' : ''}>
                  <h2>Get In Touch</h2>

                  <form onSubmit={handleSubmit} aria-label='Contact form'>
                    <Row>
                      <Col size={12} sm={6} className='px-1'>
                        <label htmlFor='first-name' className='visually-hidden'>First Name</label>
                        <input
                          id='first-name'
                          type='text'
                          value={formDetails.firstName}
                          placeholder='First Name'
                          onChange={(e) => onFormUpdate('firstName', e.target.value)}
                          autoComplete='given-name'
                          required
                        />
                      </Col>

                      <Col size={12} sm={6} className='px-1'>
                        <label htmlFor='last-name' className='visually-hidden'>Last Name</label>
                        <input
                          id='last-name'
                          type='text'
                          value={formDetails.lastName}
                          placeholder='Last Name'
                          onChange={(e) => onFormUpdate('lastName', e.target.value)}
                          autoComplete='family-name'
                        />
                      </Col>

                      <Col size={12} sm={6} className='px-1'>
                        <label htmlFor='email' className='visually-hidden'>Email Address</label>
                        <input
                          id='email'
                          type='email'
                          value={formDetails.email}
                          placeholder='Email Address'
                          onChange={(e) => onFormUpdate('email', e.target.value)}
                          autoComplete='email'
                          required
                        />
                      </Col>

                      <Col size={12} sm={6} className='px-1'>
                        <label htmlFor='phone' className='visually-hidden'>Phone Number</label>
                        <input
                          id='phone'
                          type='tel'
                          value={formDetails.phone}
                          placeholder='Phone No.'
                          maxLength={14}
                          inputMode='numeric'
                          autoComplete='tel'
                          onChange={(e) => onFormUpdate('phone', e.target.value.replace(/[^\d]/g, '').slice(0, 10))}
                        />
                      </Col>

                      <Col size={12} className='px-1'>
                        <label htmlFor='message' className='visually-hidden'>Message</label>
                        <textarea
                          id='message'
                          rows='6'
                          value={formDetails.message}
                          placeholder='Message'
                          onChange={(e) => onFormUpdate('message', e.target.value)}
                          autoComplete='off'
                          required
                        ></textarea>

                        <button type='submit' disabled={submitting}>
                          <span>{buttonText}</span>
                        </button>
                      </Col>
                    </Row>
                  </form>
                </div>
              )}
            </TrackVisibility>
          </Col>
        </Row>

        <ToastContainer />
      </Container>
    </section>
  );
};
